import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { devices, api } from '@/lib/api';
import { Topbar } from '@/components/layout/Topbar';
import { Activity, Wifi, AlertTriangle, Phone, RefreshCw, Cpu, TrendingUp, Globe } from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devices.list({ per_page: 100 }),
  });

  const list = data?.data ?? [];

  // Server-side aggregated stats (vrais counts sur toute la fleet, pas juste la page)
  const { data: stats } = useQuery({
    queryKey: ['devices-stats'],
    queryFn: () => api.get<{
      total: number; online: number; offline: number; linked: number;
      orphans: number; adopted: number; provisioned: number; nat: number;
    }>('/stats/devices').then((r) => r.data),
    refetchInterval: 15000,
  });
  // Composite state : cross-référence TR-069 inform + L2 OLT + probe network.
  // online = TR-069 actif (Inform récent < 90min)
  // unsync = network OK (ping/L2) MAIS TR-069 stale (Inform > 90min)
  // offline = unreachable (rien ne répond)
  const { data: comp } = useQuery({
    queryKey: ['devices-composite'],
    queryFn: () => api.get<{ total: number; online: number; unsync: number; offline: number }>('/stats/devices-composite').then((r) => r.data),
    refetchInterval: 30000,
  });
  const total = stats?.total ?? 0;
  const online = comp?.online ?? stats?.online ?? 0;
  const unsync = comp?.unsync ?? 0;
  const offline = comp?.offline ?? stats?.offline ?? 0;
  const linked = stats?.linked ?? 0;
  const orphans = stats?.orphans ?? 0;

  return (
    <>
      <Topbar breadcrumb={<><span className="font-semibold text-text">NOC</span> <span className="text-text-faint mx-1">/</span> <span>Vue d'ensemble</span></>} />

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Stats row */}
        <div className="grid grid-cols-6 gap-3">
          <Stat icon={Cpu}        label="CPE total"   value={total}      sub={isLoading ? '…' : `${online + unsync + offline} actifs en DB`} />
          <Stat icon={Activity}   label="Online"      value={online}     sub={`${total ? Math.round(100 * online / total) : 0}% · TR-069 OK`}
                valueColor="text-success" />
          <Stat icon={AlertTriangle} label="Unsync ⚠" value={unsync}     sub="Network OK, TR-069 stale — Inform en retard"
                valueColor={unsync > 0 ? 'text-warning' : 'text-text'} />
          <Stat icon={AlertTriangle} label="Offline"  value={offline}    sub="aucun signal réseau"
                valueColor={offline > 0 ? 'text-danger' : 'text-text'} />
          <Stat icon={Wifi}       label="Liés"        value={linked}     sub={`${total ? Math.round(100 * linked / total) : 0}% customer`} />
          <Stat icon={RefreshCw}  label="Orphelins"   value={orphans}    sub="non-assignés"
                valueColor={orphans > 0 ? 'text-warning' : 'text-text'} />
        </div>

        {/* Internet Health par catégorie */}
        <InternetHealthCard />

        {/* Infra interne par type (DNS, VoIP, IPTV, mail, etc.) */}
        <InfraInternalCard />

        {/* Device growth chart */}
        <DeviceGrowthCard />

        {/* Tables row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="card">
            <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold text-sm">Devices récents</h2>
              <span className="text-text-faint text-xs">{list.length} total</span>
            </div>
            <table className="luc-table">
              <thead><tr>
                <th style={{ width: 30 }}></th>
                <th>Sériel</th>
                <th>Modèle</th>
                <th>Customer</th>
                <th>Firmware</th>
                <th className="text-right">Last inform</th>
              </tr></thead>
              <tbody>
                {list.slice(0, 12).map((d) => (
                  <tr key={d.id}>
                    <td><span className={`dot ${d.is_online ? 'dot-up' : 'dot-down'}`} /></td>
                    <td className="font-mono text-[12px]">{d.serial_number}</td>
                    <td className="text-text-muted">{d.model?.model_name ?? '—'}</td>
                    <td>{d.customer?.name ?? <span className="text-text-faint italic">non lié</span>}</td>
                    <td className="font-mono text-[12px] text-text-muted">{d.firmware_version ?? '—'}</td>
                    <td className="text-right font-mono text-[12px] text-text-muted">{formatRelTime(d.last_inform_at)}</td>
                  </tr>
                ))}
                {!isLoading && list.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-text-faint">Aucun device — branche un CPE et reviens</td></tr>
                )}
                {isLoading && (
                  <tr><td colSpan={6} className="text-center py-8 text-text-faint">Chargement…</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <SystemActivityCard total={total} />
        </div>
      </div>
    </>
  );
}

function SystemActivityCard({ total }: { total: number }) {
  const { data } = useQuery({
    queryKey: ['system-metrics'],
    queryFn: () => api.get<{
      load: { '1m': number; '5m': number; '15m': number };
      cores: number;
      load_pct_1m: number;
      mem: { total: number; used: number; available: number; used_pct: number };
    }>('/stats/system').then((r) => r.data),
    refetchInterval: 10000,
  });

  const fmtGB = (b: number) => (b / 1024 / 1024 / 1024).toFixed(1) + ' GB';
  const loadColor = (pct: number) => pct < 50 ? 'text-success' : pct < 80 ? 'text-warning' : 'text-danger';
  const memColor = (pct: number) => pct < 70 ? 'text-success' : pct < 90 ? 'text-warning' : 'text-danger';

  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border">
        <h2 className="font-semibold text-sm">Activité système</h2>
      </div>
      <div className="p-4 space-y-3 text-sm">
        {/* CPU load avg */}
        <div className="flex items-center justify-between">
          <span className="text-text-muted">Load (1/5/15min)</span>
          <span className={`font-mono text-xs ${loadColor(data?.load_pct_1m ?? 0)}`}>
            {data ? `${data.load['1m'].toFixed(2)} / ${data.load['5m'].toFixed(2)} / ${data.load['15m'].toFixed(2)}` : '—'}
            <span className="text-text-faint ml-1">({data?.load_pct_1m ?? 0}% / {data?.cores ?? '?'} cores)</span>
          </span>
        </div>
        {/* RAM */}
        <div className="flex items-center justify-between">
          <span className="text-text-muted">RAM</span>
          <span className={`font-mono text-xs ${memColor(data?.mem.used_pct ?? 0)}`}>
            {data ? `${fmtGB(data.mem.used)} / ${fmtGB(data.mem.total)}` : '—'}
            <span className="text-text-faint ml-1">({data?.mem.used_pct ?? 0}%)</span>
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-text-muted">Scheduler sync</span>
          <span className="badge badge-up"><span className="dot dot-up" />actif 60s</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-text-muted">Mass-ops tick</span>
          <span className="badge badge-up"><span className="dot dot-up" />actif 30s</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-text-muted">filosACS CWMP</span>
          <span className="badge badge-up"><span className="dot dot-up" />UP</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-text-muted">TLS cert</span>
          <span className="badge badge-up">expire 2026-08-11</span>
        </div>
        <div className="pt-2 border-t border-border mt-2">
          <div className="text-xs text-text-faint mb-1">Tenants</div>
          <TenantsList />
        </div>
      </div>
    </div>
  );
}

function TenantsList() {
  const { data } = useQuery({
    queryKey: ['tenants-stats'],
    queryFn: async () => {
      const tenants = await api.get<Array<{ id: number; slug: string; name: string }>>('/tenants').then((r) => r.data);
      // Pour chaque tenant, fetch count devices via stats endpoint (passe X-Active-Tenant)
      // Simplification: utiliser /devices?per_page=1 et lire data.total avec tenant header
      const withCounts = await Promise.all(tenants.map(async (t) => {
        try {
          const r = await api.get<{ total: number }>(`/devices`, {
            params: { per_page: 1 },
            headers: { 'X-Active-Tenant': String(t.id) },
          });
          return { ...t, count: r.data.total };
        } catch { return { ...t, count: 0 }; }
      }));
      return withCounts;
    },
    refetchInterval: 30000,
  });

  if (!data || data.length === 0) return <span className="text-text-faint text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {data.map((t) => (
        <div key={t.id} className="badge badge-brand">
          {t.name} · {t.count} devices
        </div>
      ))}
    </div>
  );
}

function InternetHealthCard() {
  const { data } = useQuery({
    queryKey: ['internet-health'],
    queryFn: () => api.get<Array<{ category: string; total: number; ok: number; ko: number; avg_latency_ms: number | null; last_probe_at: string | null }>>('/stats/internet-health').then((r) => r.data),
    refetchInterval: 30000,
  });

  if (!data || data.length === 0) return null;

  const labels: Record<string, string> = {
    dns: 'DNS', cdn: 'CDN', streaming: 'Streaming',
    social: 'Social', cloud: 'Cloud', gaming: 'Gaming',
    workspace: 'Workspace',
  };

  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
        <h2 className="font-semibold text-sm flex items-center gap-2">
          <Globe size={13} className="text-text-muted" />Internet Health par catégorie
        </h2>
        <Link to="/app/endpoints?tab=internet" className="text-xs text-text-muted hover:text-brand">Voir tous →</Link>
      </div>
      <div className="p-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {data.map((c) => {
          const allOk = c.ko === 0;
          const latencyMs = Number(c.avg_latency_ms ?? 0);
          const latColor = latencyMs < 50 ? 'text-success' : latencyMs < 100 ? 'text-text' : latencyMs < 200 ? 'text-warning' : 'text-danger';
          return (
            <Link to={`/app/endpoints?tab=internet`} key={c.category}
                  className={`block rounded border p-2.5 hover:bg-bg-2 transition ${allOk ? 'border-success/30 bg-success/5' : 'border-danger/40 bg-danger/10'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider text-text-faint font-medium">{labels[c.category] ?? c.category}</span>
                <span className={`text-[10px] ${allOk ? 'text-success' : 'text-danger'}`}>
                  {allOk ? '✓' : `${c.ko} ✗`}
                </span>
              </div>
              <div className={`text-lg font-semibold font-mono ${latColor}`}>{latencyMs ? `${Math.round(latencyMs)}ms` : '—'}</div>
              <div className="text-[10px] text-text-faint">{c.ok}/{c.total} cibles</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function InfraInternalCard() {
  const { data } = useQuery({
    queryKey: ['infra-internal'],
    queryFn: () => api.get<Array<{ category: string; total: number; ok: number; ko: number; avg_latency_ms: number | null }>>('/stats/infra-internal').then((r) => r.data),
    refetchInterval: 30000,
  });

  if (!data || data.length === 0) return null;

  const labels: Record<string, string> = {
    dns: 'DNS interne',
    voip_pbx: 'VoIP PBX', voip_sbc: 'VoIP SBC', voip_registrar: 'VoIP Registrar', voip_media_server: 'VoIP Media',
    iptv_backend: 'IPTV Backend', iptv_middleware: 'IPTV Middleware', video_encoder: 'Encoder', pvr: 'PVR',
    mail_imap: 'Mail IMAP', mail_smtp: 'Mail SMTP', mail_webmail: 'Webmail',
    web_server: 'Web', api_service: 'API',
    olt: 'OLT', router_core: 'Router Core', firewall: 'Firewall',
    cdn_cache: 'Cache',
  };

  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
        <h2 className="font-semibold text-sm flex items-center gap-2">
          <Activity size={13} className="text-text-muted" />Infrastructure interne par service
        </h2>
        <Link to="/app/endpoints?tab=infra_int" className="text-xs text-text-muted hover:text-brand">Voir tous →</Link>
      </div>
      <div className="p-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {data.map((c) => {
          const allOk = c.ko === 0;
          const latencyMs = Number(c.avg_latency_ms ?? 0);
          const latColor = latencyMs < 5 ? 'text-success' : latencyMs < 30 ? 'text-text' : latencyMs < 100 ? 'text-warning' : 'text-danger';
          return (
            <Link to="/app/endpoints?tab=infra_int" key={c.category}
                  className={`block rounded border p-2.5 hover:bg-bg-2 transition ${allOk ? 'border-success/30 bg-success/5' : 'border-danger/40 bg-danger/10'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider text-text-faint font-medium truncate">{labels[c.category] ?? c.category}</span>
                <span className={`text-[10px] ${allOk ? 'text-success' : 'text-danger'}`}>
                  {allOk ? '✓' : `${c.ko} ✗`}
                </span>
              </div>
              <div className={`text-lg font-semibold font-mono ${latColor}`}>{latencyMs ? `${Math.round(latencyMs)}ms` : '—'}</div>
              <div className="text-[10px] text-text-faint">{c.ok}/{c.total} services</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function DeviceGrowthCard() {
  // hours plutôt que days pour pouvoir avoir 1j (24h) ou 30j (720h)
  const [hours, setHours] = useState<number>(24);
  const days = Math.max(1, Math.round(hours / 24));

  const { data: growth } = useQuery({
    queryKey: ['devices-growth', days],
    queryFn: () => api.get<Array<{ day: string; total: number; adopted: number; provisioned: number }>>('/stats/devices-growth', { params: { days } }).then(r => r.data),
    refetchInterval: 60000,
  });

  const { data: statusHist } = useQuery({
    queryKey: ['devices-status-history', hours],
    queryFn: () => api.get<Array<{ bucket: string; total: number; online: number; offline: number; unsync: number }>>('/stats/devices-status-history', { params: { hours } }).then(r => r.data),
    refetchInterval: 60000,
  });

  // Merge growth + status history par jour (le bucket de status est trunqué hourly|daily côté backend)
  const byKey: Record<string, { key: string; label: string; total?: number; adopted?: number; provisioned?: number; online?: number; offline?: number; unsync?: number }> = {};
  for (const r of growth ?? []) {
    const key = r.day.slice(0, 10);
    byKey[key] = { key, label: hours <= 48 ? key.slice(5) : key.slice(5), total: Number(r.total), adopted: Number(r.adopted), provisioned: Number(r.provisioned) };
  }
  for (const s of statusHist ?? []) {
    // bucket = ISO timestamp ; granularité 5min sur 1d, hour sur 7d, day sinon
    const key = hours <= 24 ? s.bucket.slice(0, 16) : hours <= 48 ? s.bucket.slice(0, 13) : s.bucket.slice(0, 10);
    const label = hours <= 24 ? s.bucket.slice(11, 16) : hours <= 48 ? s.bucket.slice(11, 13) + 'h' : s.bucket.slice(5, 10);
    byKey[key] = {
      ...(byKey[key] ?? { key, label }),
      key, label,
      online: Number(s.online),
      offline: Number(s.offline),
      unsync: Number(s.unsync ?? 0),
      total: byKey[key]?.total ?? Number(s.total),
    };
  }
  const chartData = Object.values(byKey).sort((a, b) => a.key.localeCompare(b.key));

  const current = chartData[chartData.length - 1]?.total ?? 0;
  const prev = chartData[0]?.total ?? 0;
  const delta = current - prev;
  const lastOffline = [...chartData].reverse().find(r => r.offline != null)?.offline ?? 0;

  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp size={13} className="text-text-muted" />
          <h2 className="font-semibold text-sm">Évolution flotte CPE</h2>
          {delta > 0 && <span className="text-xs text-success">+{delta}</span>}
          {lastOffline > 0 && <span className="text-xs text-danger">·{lastOffline} offline</span>}
        </div>
        <div className="flex items-center gap-1 bg-bg-2 border border-border rounded text-xs">
          {[
            { h: 24, label: '1j' },
            { h: 24 * 7, label: '7j' },
            { h: 24 * 30, label: '30j' },
            { h: 24 * 90, label: '90j' },
          ].map((opt) => (
            <button
              key={opt.h}
              onClick={() => setHours(opt.h)}
              className={`h-6 px-2 ${hours === opt.h ? 'bg-surface-hi text-text' : 'text-text-muted hover:text-text'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className="p-3">
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="grad-total" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#56b4e9" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#56b4e9" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="grad-online" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="grad-offline" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#ef4444" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="grad-unsync" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#2e3038" strokeDasharray="2 2" />
            <XAxis dataKey="label" stroke="#6c7079" fontSize={10} />
            <YAxis stroke="#6c7079" fontSize={10} allowDecimals={false} />
            <Tooltip contentStyle={{ background: '#21232a', border: '1px solid #3a3c46', fontSize: 11 }} />
            <Area type="monotone" dataKey="total"   stroke="#56b4e9" strokeWidth={1.8} fill="url(#grad-total)"   name="Total" />
            <Area type="monotone" dataKey="online"  stroke="#10b981" strokeWidth={1.5} fill="url(#grad-online)"  name="Online (TR-069 ok)" />
            <Area type="monotone" dataKey="unsync"  stroke="#f59e0b" strokeWidth={1.5} fill="url(#grad-unsync)"  name="Unsync (network OK, TR-069 stale)" />
            <Area type="monotone" dataKey="offline" stroke="#ef4444" strokeWidth={1.5} fill="url(#grad-offline)" name="Offline (aucun signal)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, valueColor = 'text-text' }:
  { icon: any; label: string; value: React.ReactNode; sub?: string; valueColor?: string }) {
  return (
    <div className="stat-tile">
      <div className="flex items-center justify-between">
        <span className="stat-label">{label}</span>
        <Icon size={12} className="text-text-faint" />
      </div>
      <span className={`stat-value ${valueColor}`}>{value}</span>
      {sub && <span className="stat-sub">{sub}</span>}
    </div>
  );
}

function formatRelTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)   return `${diff}s`;
  if (diff < 3600) return `${Math.round(diff/60)}min`;
  if (diff < 86400)return `${Math.round(diff/3600)}h`;
  return `${Math.round(diff/86400)}j`;
}
